<script setup lang="ts">
import { hospitalityGroup } from "@mmh/mmh/src/config"
import { ExternalLink } from "lucide-vue-next"
import type { HTMLAttributes } from "vue"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { AspectRatio } from "~/components/ui/aspect-ratio"
import { Separator } from "~/components/ui/separator"
import { useLocation } from "~/composables/locations"
import { titleCase } from "~/lib/case"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location } = useLocation()
</script>

<template>
    <section
        v-if="location"
        id="hospitality-group"
        tabindex="-1"
        :class="cn('space-y-24', cl)"
    >
        <div class="space-y-12">
            <header class="space-y-6 text-center">
                <h2 class="font-headline text-3xl md:text-4xl">
                    {{ location?.pageContent?.hospitalityGroup?.title || 'Memento Mori Hospitality' }}
                </h2>
            </header>
            <div class="flex flex-col md:flex-row min-h-[350px] gap-6">
                <div class="flex-1 min-h-[250px] lg:min-h-full">
                    <ThemeImg
                        :src="location?.pageContent?.hospitalityGroup?.image || '/global/hospitality-group/jackson.jpg'"
                        :alt="location?.pageContent?.hospitalityGroup?.imageAlt || ''"
                        class="w-full h-full object-cover object-center"
                    />
                </div>
                <div class="flex-1 flex items-center">
                    <div class="w-full px-2">
                        <div class="space-y-6 md:space-y-7">
                            <div
                                class="space-y-4 md:space-y-5"
                                role="article"
                            >
                                <header class="space-y-2">
                                    <h3 class="text-2xl md:text-3xl leading-tight">
                                        Jackson Kalb
                                    </h3>
                                    <p class="uppercase text-sm font-medium tracking-wider">
                                        EXECUTIVE CHEF & OWNER
                                    </p>
                                </header>
                                
                                <div 
                                    class="space-y-4 leading-relaxed prose prose-sm max-w-none"
                                    v-html="location?.pageContent?.hospitalityGroup?.content || ''"
                                />
                                
                                <blockquote class="border-l-2 border-white/40 pl-4 italic text-sm md:text-base leading-relaxed">
                                    "Remember that you will die" is the translation of the Latin phrase "Memento Mori."
                                    We use this phrase as the basis of our hospitality company not to be morbid, to think
                                    negatively or to live in fear, but rather to treat each day and each moment as a blessing. 
                                    We are here now, we are guaranteed to be gone one day, and we don't know when that day is 
                                    coming, so it is a gentle reminder to spend time wisely rather than waste it.
                                </blockquote>
                            </div>
                            
                            <div class="pt-4">
                                <ThemeButton
                                    size="lg"
                                    as-child
                                >
                                    <a
                                        href="https://mmh.co"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        tabindex="0"
                                        aria-label="Navigate to Memento Mori Hospitality website to learn more"
                                        class="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                                    >
                                        Visit Memento Mori Hospitality
                                    </a>
                                </ThemeButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="space-y-12">
            <header class="space-y-6">
                <h3 class="text-xl font-headline text-center">
                    Our Restaurants
                </h3>
                <Separator class="w-10 mx-auto bg-foreground/20" />
            </header>
            <div class="grid sm:grid-cols-2 gap-x-6 gap-y-8 md:gap-y-10">
                <div 
                    v-for="(concept, i) in hospitalityGroup.concepts" 
                    :key="i" 
                    class="flex flex-col h-full"
                >
                    <div class="flex-grow space-y-6">
                        <div class="group space-y-4">
                            <AspectRatio
                                :ratio="4 / 2"
                                class="w-full overflow-hidden"
                            >
                                <ThemeImg 
                                    :src="concept.thumbUrl" 
                                    :alt="`${titleCase(concept.name)} restaurant`"
                                    class="size-full object-cover shadow-md transition-transform duration-500 group-hover:scale-105"
                                />
                            </AspectRatio>
                            <a 
                                :href="concept.website || '#'" 
                                target="_blank"
                                rel="noopener noreferrer"
                                tabindex="0"
                                :aria-label="`Visit ${concept.name} website`"
                                class="w-fit flex items-center gap-2 font-button text-sm relative after:absolute after:-bottom-1 after:bg-foreground after:h-px after:w-0 after:transition-all after:duration-500 group-hover:after:w-full rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                            >
                                <span class="capsize">
                                    {{ titleCase(concept.name) }}
                                </span>
                                <ExternalLink class="size-3 text-foreground-muted transition-colors group-hover:text-foreground" />
                            </a>
                        </div>
                        <p class="text-sm text-foreground-muted-darker">
                            {{ concept.about }}
                        </p>                    
                        <div
                            v-if="!concept.locations || concept.locations.length <= 1"
                            class="space-y-1"
                        >
                            <header>
                                <h3 class="text-sm font-medium text-foreground-muted uppercase">
                                    Address
                                </h3>
                            </header>
                            <div class="flex flex-col">
                                <p class="text-sm">
                                    {{ concept.locations?.[0]?.address?.[0] }}, 
                                    {{ concept.locations?.[0]?.address?.[1] }}
                                </p>
                            </div>
                        </div>
                        <div
                            v-if="concept.locations && concept.locations.length > 1"
                            class="space-y-1"
                        >
                            <header>
                                <h3 class="text-sm font-medium text-foreground-muted uppercase">
                                    Locations
                                </h3>
                            </header>
                            <div class="flex flex-col gap-0.5">
                                <div
                                    v-for="l of concept.locations"
                                    :key="l.name"
                                    class="group"
                                >
                                    <a
                                        v-if="!l.comingSoon"
                                        :href="l.url"
                                        class="inline-flex items-center gap-2 font-button text-sm relative after:absolute after:-bottom-1 after:bg-foreground after:h-px after:w-0 after:transition-all after:duration-500 group-hover:after:w-full rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        tabindex="0"
                                        :aria-label="`View ${titleCase(l.name)} website`"
                                    >
                                        <span class="capsize">{{ titleCase(l.name) }}</span>
                                        <ExternalLink class="size-3 text-foreground-muted transition-colors group-hover:text-foreground" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
